// src/pages/Sejour.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import useSpeechToText from "../hooks/useSpeechToText";
import VoiceDictation from "../components/VoiceDictation";
import useMicLevel from "../hooks/useMicLevel";
import {
  MapPin,
  Calendar as CalendarIcon,
  Users,
  Star,
  Wifi,
  Car,
  Coffee,
  Dumbbell,
  Send,
  User,
  Mail,
  Phone,
  Home,
  BadgePercent,
  Wallet,
  CheckCircle2,
} from "lucide-react";
import toast from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import { useAuth } from "../contexts/AuthContext";
import reservationHotelService from "../services/reservationHotelService";
import { useSearchParams } from "react-router-dom";
import zelijPattern from "../assets/zelij3.png";

// ✅ important: use translation function (same as HeroSection)
import { t as tr } from "../contexts/translations";

/* =========================
   API BASE (no hardcoded URLs)
========================= */
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  "https://nouzhaback-theta.vercel.app";

// ---------- Services (checkbox) ----------
const HOTEL_SERVICES = [
  { key: "Wifi", label: "Wifi", icon: Wifi },
  { key: "Parking", label: "Parking", icon: Car },
  { key: "Restaurant", label: "Restaurant", icon: Coffee },
  { key: "Spa", label: "Spa", icon: Dumbbell },
  { key: "Piscine", label: "Piscine", icon: Users },
  { key: "Plage privée", label: "Plage privée", icon: MapPin },
  { key: "Golf", label: "Golf", icon: MapPin },
  { key: "Sauna", label: "Sauna", icon: Dumbbell },
];

const HOTEL_SERVICE_TRANSLATION_KEYS = {
  Wifi: "serviceWifi",
  Parking: "serviceParking",
  Restaurant: "serviceRestaurant",
  Spa: "serviceSpa",
  Piscine: "servicePool",
  "Plage privée": "servicePrivateBeach",
  Golf: "serviceGolf",
  Sauna: "serviceSauna",
};

// ---------- Utils dates ----------
const getToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
};

const parseISO = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDisplay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d.toString().padStart(2, "0")}/${m.toString().padStart(2, "0")}/${y}`;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const isBefore = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const db = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return da.getTime() < db.getTime();
};

const buildMonthDays = (year, month) => {
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
};

// ---------- Custom DatePicker ----------
// eslint-disable-next-line no-unused-vars
const CustomDatePicker = ({ value, onChange, min, language, T }) => {
  const [isOpen, setIsOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDate = parseISO(value);
  const minDate = parseISO(min || getToday());

  const initial = selectedDate || today;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (date) => {
    if (isBefore(date, minDate)) return;
    const iso = date.toISOString().split("T")[0];
    onChange({ target: { value: iso } });
    setIsOpen(false);
  };

  const goMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  const monthDays = buildMonthDays(viewYear, viewMonth);

  const locale =
    language === "en" ? "en-US" : language === "ar" ? "ar-MA" : "fr-FR";

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });

  const displayValue = value ? formatDisplay(value) : "";

  return (
    <div className="relative" ref={wrapperRef}>
      <div
        className="w-full pl-9 pr-4 py-3 border-2 border-orange-200 rounded-xl
                   focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400
                   bg-white/70 flex items-center cursor-pointer"
        onClick={() => setIsOpen((o) => !o)}
      >
        <CalendarIcon className="absolute left-3 text-orange-600 w-4 h-4" />
        <span className={displayValue ? "text-gray-900 text-sm" : "text-gray-400 text-sm"}>
          {displayValue || T("dpPlaceholder", "jj/mm/aaaa")}
        </span>
      </div>

      {isOpen && (
        <div className="absolute mt-2 z-30 bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-80">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              className="p-1 rounded-full hover:bg-gray-100"
              onClick={() => goMonth(-1)}
            >
              ‹
            </button>
            <div className="font-semibold text-gray-800 capitalize">{monthLabel}</div>
            <button
              type="button"
              className="p-1 rounded-full hover:bg-gray-100"
              onClick={() => goMonth(1)}
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-500 mb-1">
            <div>{T("dpMon", "lu")}</div>
            <div>{T("dpTue", "ma")}</div>
            <div>{T("dpWed", "me")}</div>
            <div>{T("dpThu", "je")}</div>
            <div>{T("dpFri", "ve")}</div>
            <div className="text-orange-500">{T("dpSat", "sa")}</div>
            <div className="text-orange-500">{T("dpSun", "di")}</div>
          </div>

          <div className="grid grid-cols-7 gap-1 text-sm">
            {monthDays.map((date, idx) => {
              if (!date) return <div key={idx} />;

              const disabled = isBefore(date, minDate);
              const isSelected = isSameDay(date, selectedDate);
              const isToday = isSameDay(date, today);

              let classes = "w-9 h-9 flex items-center justify-center rounded-lg transition-all";
              if (disabled) classes += " text-gray-300 cursor-not-allowed";
              else classes += " cursor-pointer hover:bg-orange-100 text-gray-800";
              if (isToday && !isSelected) classes += " border border-orange-300";
              if (isSelected) classes += " bg-orange-600 text-white hover:bg-orange-600 font-semibold";

              return (
                <button
                  key={idx}
                  type="button"
                  className={classes}
                  disabled={disabled}
                  onClick={() => handleSelect(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="flex justify-between mt-3 text-xs">
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700"
              onClick={() => onChange({ target: { value: "" } })}
            >
              {T("dpClear", "Effacer")}
            </button>
            <button
              type="button"
              className="text-orange-600 hover:text-orange-700 font-semibold"
              onClick={() => handleSelect(today)}
            >
              {T("dpToday", "Aujourd’hui")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DateField = ({ label, value, onChange, min, className = "" }) => (
  <div className={`relative ${className}`}>
    <label className="block font-medium mb-2 text-gray-700">{label}</label>
    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400">
      <CalendarIcon className="mr-2 h-5 w-5 text-orange-500" />
      <input
        type="date"
        value={value}
        onChange={onChange}
        min={min}
        className="w-full outline-none bg-transparent"
      />
    </div>
  </div>
);

// ---------- Main page ----------
const SejourPage = ({ language }) => {
  const { currentUser } = useAuth();

  // safe translation helper (fallback when key missing)
  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  const today = useMemo(() => getToday(), []);

  // ---------- Speech-to-Text (Web Speech API) ----------
  const stt = useSpeechToText({
    lang: language === "ar" ? "ar-MA" : language === "en" ? "en-US" : "fr-FR",
    continuous: true,
    interimResults: true,
  });

  useMicLevel(stt.listening);

  useEffect(() => {
    if (!stt.supported) return;
    const full = (stt.transcript + (stt.interim ? " " + stt.interim : "")).trim();
    if (full) console.log("[STT Sejour] =>", full);
  }, [stt.supported, stt.transcript, stt.interim]);

  // ---------- Form states ----------
  const [ville, setVille] = useState("");
  const [dateArrivee, setDateArrivee] = useState("");
  const [dateDepart, setDateDepart] = useState("");
  const [nbInvites, setNbInvites] = useState(1);

  // Pré-remplissage depuis les dates choisies dans le hero (?dateArrivee&dateDepart).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const a = searchParams.get("dateArrivee");
    const d = searchParams.get("dateDepart");
    if (a) setDateArrivee(a);
    if (d) setDateDepart(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [categorieEtoiles, setCategorieEtoiles] = useState(""); // "", "1".."5"
  const [prixMin, setPrixMin] = useState(50);
  const [prixMax, setPrixMax] = useState(500);

  const [services, setServices] = useState([]);
  const toggleService = (key) => {
    setServices((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const [souhaiteReduction, setSouhaiteReduction] = useState(false);

  const [nomComplet, setNomComplet] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [adresse, setAdresse] = useState("");

  const [acceptCgv, setAcceptCgv] = useState(false);
  const [formStep, setFormStep] = useState(0);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const SERVICE_KEYS = useMemo(() => HOTEL_SERVICES.map((s) => s.key), []);

  const applySejourFields = (fields) => {
    if (!fields) return;

    if (fields.ville) setVille(String(fields.ville).trim());

    if (fields.dateArrivee) setDateArrivee(fields.dateArrivee);
    if (fields.dateDepart) setDateDepart(fields.dateDepart);

    if (fields.nbInvites != null) setNbInvites(Number(fields.nbInvites));

    if (fields.categorieEtoiles != null) {
      setCategorieEtoiles(String(fields.categorieEtoiles));
    }

    if (fields.prixParNuit?.min != null) setPrixMin(Number(fields.prixParNuit.min));
    if (fields.prixParNuit?.max != null) setPrixMax(Number(fields.prixParNuit.max));

    if (Array.isArray(fields.servicesSouhaites)) {
      const cleaned = fields.servicesSouhaites
        .map((x) => String(x).trim())
        .filter((x) => SERVICE_KEYS.includes(x));
      setServices(cleaned);
    }

    if (typeof fields.souhaiteReduction === "boolean") {
      setSouhaiteReduction(fields.souhaiteReduction);
    }

    if (fields.client?.nomComplet) setNomComplet(fields.client.nomComplet);
    if (fields.client?.email && !currentUser) setEmail(fields.client.email);
    if (fields.client?.telephone) setTelephone(fields.client.telephone);
    if (fields.client?.adresse) setAdresse(fields.client.adresse);
  };

  const handleGeminiAutofill = async () => {
    const spoken = (stt.transcript + (stt.interim ? " " + stt.interim : "")).trim();
    if (!spoken) return toast.error(T("sejourSpeakFirst", "Parle d'abord, puis clique sur Autofill."));

    try {
      const res = await fetch(`${API_BASE}/api/ai/parse-sejour`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: spoken,
          lang: language,
          allowedServices: HOTEL_SERVICES.map((s) => s.key),
        }),
      });

      const data = await res.json();
      if (!data?.success) {
        toast.error(data?.message || T("geminiError", "Erreur Gemini"));
        console.log("[GEMINI Sejour ERROR]", data);
        return;
      }

      applySejourFields(data.fields);
      toast.success(T("geminiAutofillSuccess", "Formulaire séjour rempli à partir de ta voix ✅"));
    } catch (e) {
      console.error(e);
      toast.error(T("geminiNetworkError", "Erreur réseau / serveur Gemini"));
    }
  };

  useEffect(() => {
    setEmail(currentUser?.email || "");
  }, [currentUser]);

  const validate = () => {
    if (!ville.trim()) {
      toast.error(T("errCityRequired", "Veuillez saisir la ville."));
      return false;
    }
    if (!dateArrivee) {
      toast.error(T("errArrivalRequired", "Veuillez choisir la date d’arrivée."));
      return false;
    }
    if (!dateDepart) {
      toast.error(T("errDepartureRequired", "Veuillez choisir la date de départ."));
      return false;
    }

    const a = new Date(dateArrivee);
    const d = new Date(dateDepart);
    if (a >= d) {
      toast.error(T("errDepartureAfterArrival", "La date de départ doit être après la date d’arrivée."));
      return false;
    }

    if (!nomComplet.trim()) {
      toast.error(T("errFullNameRequired", "Veuillez saisir votre nom complet."));
      return false;
    }
    if (!email.trim()) {
      toast.error(T("errEmailRequired", "Veuillez saisir votre email."));
      return false;
    }
    if (!telephone.trim()) {
      toast.error(T("errPhoneRequired", "Veuillez saisir votre téléphone."));
      return false;
    }
    if (!adresse.trim()) {
      toast.error(T("errAddressRequired", "Veuillez saisir votre adresse."));
      return false;
    }

    if (!acceptCgv) {
      toast.error(T("errAcceptCgvRequired", "Veuillez accepter les conditions de paiement (CMI/CGV)."));
      return false;
    }

    if (prixMin < 0 || prixMax < 0 || prixMin > prixMax) {
      toast.error(T("errPriceRange", "Veuillez vérifier l’intervalle de prix."));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Garde-fou wizard : avancer tant qu'on n'est pas à la dernière étape
    if (formStep !== lastFormStep) { formNext(); return; }
    if (!validate()) return;

    const payload = {
      ville: ville.trim(),
      dateArrivee,
      dateDepart,
      nbInvites: Number(nbInvites),
      categorieEtoiles: categorieEtoiles ? Number(categorieEtoiles) : null,
      prixParNuit: { min: Number(prixMin), max: Number(prixMax) },
      servicesSouhaites: services,
      souhaiteReduction,
      client: {
        nomComplet: nomComplet.trim(),
        email: email.trim(),
        telephone: telephone.trim(),
        adresse: adresse.trim(),
      },
      acceptCgv,
      statut: "EN_ATTENTE",
      compteId: currentUser?._id || null,
      lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
    };

    try {
      setLoading(true);

      const res = await reservationHotelService.createRequest(payload);

      if (res?.success === false) {
        toast.error(res?.message || "Erreur lors de l’envoi.");
        return;
      }

      toast.success(res?.message || "Votre demande a été envoyée avec succès !");
      setSuccess(true);

      setVille("");
      setDateArrivee("");
      setDateDepart("");
      setNbInvites(1);
      setCategorieEtoiles("");
      setPrixMin(50);
      setPrixMax(500);
      setServices([]);
      setSouhaiteReduction(false);
      setNomComplet("");
      setTelephone("");
      setAdresse("");
      setAcceptCgv(false);
      if (!currentUser) setEmail("");
    } catch (err) {
      console.error("DEMANDE SEJOUR ERROR:", err?.response?.data || err);
      notifyApiError(err, "Erreur lors de l’envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // ===== Wizard (demande de séjour) =====
  const FORM_STEPS = [
    { label: T("wizStay", "Séjour"), icon: MapPin },
    { label: T("wizInfos", "Vos infos"), icon: User },
    { label: T("wizPayment", "Paiement"), icon: Wallet },
  ];
  const lastFormStep = FORM_STEPS.length - 1;

  const validateFormStep = (s) => {
    if (s === 0) {
      if (!ville.trim()) return T("errCityRequired", "Veuillez saisir la ville.");
      if (!dateArrivee) return T("errArrivalRequired", "Veuillez choisir la date d’arrivée.");
      if (!dateDepart) return T("errDepartureRequired", "Veuillez choisir la date de départ.");
      if (new Date(dateArrivee) >= new Date(dateDepart))
        return T("errDepartureAfterArrival", "La date de départ doit être après la date d’arrivée.");
    }
    if (s === 1) {
      if (!nomComplet.trim()) return T("errFullNameRequired", "Veuillez saisir votre nom complet.");
      if (!email.trim()) return T("errEmailRequired", "Veuillez saisir votre email.");
      if (!telephone.trim()) return T("errPhoneRequired", "Veuillez saisir votre téléphone.");
      if (!adresse.trim()) return T("errAddressRequired", "Veuillez saisir votre adresse.");
    }
    return null;
  };
  const formNext = () => {
    const err = validateFormStep(formStep);
    if (err) { toast.error(err); return; }
    setFormStep((s) => Math.min(lastFormStep, s + 1));
  };
  const formBack = () => setFormStep((s) => Math.max(0, s - 1));

  return (
    <div
      className="nouzha-premium-skin py-24 min-h-screen relative overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)" }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.4,
        }}
      />
      <div className="absolute inset-0 bg-white/80 z-0 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-orange-700 mb-4 drop-shadow-lg">
            {T("sejourTitle", "Séjour & Hébergement")}
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {T(
              "sejourSubtitleNew",
              "Déposez votre demande (hôtel/riad) via le formulaire. Notre équipe vous proposera la meilleure offre et vous enverra un lien de paiement sécurisé."
            )}
          </p>
        </motion.div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/90 rounded-3xl shadow-xl p-8 text-center max-w-2xl mx-auto"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {T("sejourSuccessTitle", "Demande envoyée avec succès !")}
            </h2>
            <p className="text-gray-600 mb-6">
              {T(
                "sejourSuccessDesc",
                "Nous avons bien reçu votre demande de séjour. Notre agent va l’étudier et vous contactera par email avec une proposition et un lien de paiement."
              )}
            </p>
            <button
              onClick={() => setSuccess(false)}
              className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all font-semibold"
            >
              {T("sejourAnotherRequest", "Faire une autre demande")}
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.15 }}
            onSubmit={handleSubmit}
            className="rounded-[2rem] p-0"
          >
            {/* ===== Stepper ===== */}
            <div className="flex items-center justify-between gap-2 mb-8 px-2">
              {FORM_STEPS.map((st, i) => {
                const Icon = st.icon;
                const done = i < formStep;
                const active = i === formStep;
                return (
                  <React.Fragment key={st.label}>
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-all duration-300 ${active ? "bg-orange-600 text-white scale-110" : done ? "bg-green-500 text-white" : "bg-white text-gray-400 border border-gray-200"}`}>
                        {done ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                      </div>
                      <span className={`mt-2 text-[11px] md:text-xs font-bold uppercase tracking-wide ${active ? "text-orange-700" : "text-gray-400"}`}>{st.label}</span>
                    </div>
                    {i < FORM_STEPS.length - 1 && (
                      <div className={`h-1 flex-1 rounded-full mb-6 transition-all duration-300 ${i < formStep ? "bg-green-500" : "bg-gray-200"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {formStep === 0 && (
            <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                  <Star className="w-4 h-4" />
                </div>
                <h2 className="text-2xl md:text-[1.9rem] leading-tight font-black uppercase tracking-tight text-[#111827]">
                  {T("sejourInfoTitle", "Informations du séjour")}
                </h2>
              </div>

              {/* Voice + Autofill (IA) */}
              <VoiceDictation
                stt={stt}
                onAutofill={handleGeminiAutofill}
                placeholder={T("sejourTranscriptionPlaceholder", "La transcription s’affiche ici...")}
                hint={T("sejourVoiceHint", "Astuce : dites « arrivée le …, départ le …, budget …, services wifi parking … ».")}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* City */}
              <div>
                <label className="block font-medium mb-2 text-gray-700">
                  {T("sejourCityLabel", "Ville souhaitée *")}
                </label>
                <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                  <MapPin className="mr-2 h-5 w-5 text-orange-500" />
                  <input
                    type="text"
                    value={ville}
                    onChange={(e) => setVille(e.target.value)}
                    placeholder={T("sejourCityPlaceholder", "Ex: Fès, Rabat, Marrakech...")}
                    className="w-full outline-none bg-transparent"
                    required
                  />
                </div>
              </div>

              {/* Guests */}
              <div>
                <label className="block font-medium mb-2 text-gray-700">
                  {T("sejourGuestsLabel", "Nombre d’invités *")}
                </label>
                <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                  <Users className="mr-2 h-5 w-5 text-orange-500" />
                  <select
                    value={String(nbInvites)}
                    onChange={(e) => setNbInvites(Number(e.target.value))}
                    className="w-full outline-none bg-transparent"
                    required
                  >
                    {[...Array(15)].map((_, i) => (
                      <option key={i} value={i + 1}>
                        {i + 1}{" "}
                        {i === 0 ? T("sejourGuestOne", "invité") : T("sejourGuestsMany", "invités")}
                      </option>
                    ))}
                    <option value="16">16+ {T("sejourGuestsMany", "invités")}</option>
                  </select>
                </div>
              </div>

              {/* Dates */}
              <DateField
                label={T("sejourArrivalLabel", "Date d’arrivée *")}
                value={dateArrivee}
                onChange={(e) => setDateArrivee(e.target.value)}
                min={today}
              />
              <DateField
                label={T("sejourDepartureLabel", "Date de départ *")}
                value={dateDepart}
                onChange={(e) => setDateDepart(e.target.value)}
                min={dateArrivee || today}
              />

              {/* Stars */}
              <div>
                <label className="block font-medium mb-2 text-gray-700">
                  {T("sejourStarsLabel", "Catégorie (étoiles)")}
                </label>
                <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                  <Star className="mr-2 h-5 w-5 text-orange-500" />
                  <select
                    value={categorieEtoiles}
                    onChange={(e) => setCategorieEtoiles(e.target.value)}
                    className="w-full outline-none bg-transparent"
                  >
                    <option value="">{T("sejourStarsAll", "Tous")}</option>
                    {[5, 4, 3, 2, 1].map((s) => (
                      <option key={s} value={s}>
                        {s} ★
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {T("sejourStarsHint", "L’agent proposera l’hébergement le plus adapté selon la disponibilité.")}
                </p>
              </div>

              {/* Budget */}
              <div>
                <label className="block font-medium mb-2 text-gray-700">
                  {T("sejourBudgetLabel", "Budget par nuit (MAD) *")}
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-16">{T("sejourMin", "Min")}</span>
                    <input
                      type="number"
                      min="0"
                      value={prixMin}
                      onChange={(e) => setPrixMin(Number(e.target.value))}
                      className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-16">{T("sejourMax", "Max")}</span>
                    <input
                      type="number"
                      min="0"
                      value={prixMax}
                      onChange={(e) => setPrixMax(Number(e.target.value))}
                      className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {T("sejourBudgetHint", "Indique un intervalle réaliste pour accélérer le traitement.")}
                </p>
              </div>
              </div>
            </section>
            )}

            {formStep === 1 && (
            <>
            {/* Services desired */}
            <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                  <Coffee className="w-4 h-4" />
                </div>
                <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">
                  {T("sejourServicesTitle", "Services souhaités dans l’hébergement")}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {HOTEL_SERVICES.map(({ key, label, icon: Icon }) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 p-3 rounded-2xl border border-orange-100 bg-orange-50/40 hover:bg-orange-50 transition cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={services.includes(key)}
                      onChange={() => toggleService(key)}
                      className="h-4 w-4 accent-orange-600"
                    />
                    <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center border border-orange-200">
                      <Icon className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="text-gray-800 font-medium">{T(HOTEL_SERVICE_TRANSLATION_KEYS[key], label)}</span>
                  </label>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3 p-4 bg-white border border-orange-200 rounded-2xl">
                <input
                  type="checkbox"
                  checked={souhaiteReduction}
                  onChange={(e) => setSouhaiteReduction(e.target.checked)}
                  className="h-4 w-4 accent-orange-600"
                />
                <BadgePercent className="w-5 h-5 text-orange-600" />
                <span className="text-sm text-gray-800">
                  {T("sejourDiscountLabel", "Je souhaite recevoir une offre avec réduction si disponible")}
                </span>
              </div>
            </section>

            {/* Personal Info */}
            <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                  <User className="w-4 h-4" />
                </div>
                <h2 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">
                  {T("sejourPersonalInfoTitle", "Informations personnelles")}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full name */}
                <div>
                  <label className="block font-medium mb-2 text-gray-700">
                    {T("sejourFullNameLabel", "Nom complet *")}
                  </label>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                    <User className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="text"
                      value={nomComplet}
                      onChange={(e) => setNomComplet(e.target.value)}
                      className="w-full outline-none bg-transparent"
                      placeholder={T("sejourFullNamePlaceholder", "Ex: Nom complet")}
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block font-medium mb-2 text-gray-700">
                    {currentUser ? T("sejourEmailAccountLabel", "Email (compte)") : T("sejourEmailLabel", "Email *")}
                  </label>

                  <div
                    className={`flex items-center border-2 rounded-xl px-4 py-3 ${
                      currentUser
                        ? "bg-gray-100 border-gray-200"
                        : "bg-white/70 border-orange-200 focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400"
                    }`}
                  >
                    <Mail className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full outline-none bg-transparent ${
                        currentUser ? "cursor-not-allowed text-gray-600" : ""
                      }`}
                      placeholder="vous@example.com"
                      readOnly={!!currentUser}
                      disabled={!!currentUser}
                      required
                    />
                  </div>

                  {currentUser && (
                    <p className="mt-1 text-sm text-gray-500">
                      {T("sejourEmailAccountHint", "Cette adresse est liée à votre compte et ne peut pas être modifiée ici.")}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block font-medium mb-2 text-gray-700">
                    {T("sejourPhoneLabel", "Téléphone *")}
                  </label>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                    <Phone className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="tel"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      className="w-full outline-none bg-transparent"
                      placeholder={T("sejourPhonePlaceholder", "Ex: +212 6xx xx xx xx")}
                      required
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block font-medium mb-2 text-gray-700">
                    {T("sejourAddressLabel", "Adresse *")}
                  </label>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                    <Home className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="text"
                      value={adresse}
                      onChange={(e) => setAdresse(e.target.value)}
                      className="w-full outline-none bg-transparent"
                      placeholder={T("sejourAddressPlaceholder", "Ex: Quartier, rue, ville...")}
                      required
                    />
                  </div>
                </div>
              </div>
            </section>
            </>
            )}

            {formStep === 2 && (
            <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                  <Wallet className="w-4 h-4" />
                </div>
                <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">{T("paymentConditions", "Conditions & paiement")}</h3>
              </div>

              <div className="p-4 bg-white border border-orange-200 rounded-2xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptCgv}
                  onChange={(e) => setAcceptCgv(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-orange-600"
                />
                <span className="text-sm text-gray-800">
                  {T("sejourAcceptText1", "J’ai lu et j’accepte les")}{" "}
                  <a
                    href="/cgv"
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange-600 font-semibold hover:underline"
                  >
                    {T("sejourAcceptCgvLink", "Conditions Générales de Vente")}
                  </a>{" "}
                  {T("sejourAcceptText2", "ainsi que les conditions de paiement via CMI.")}
                </span>
              </label>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">{T("sejourSecurePayment", "Paiement sécurisé :")}</span>
                <a
                  href="/paiement-securise"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-orange-600 font-semibold hover:underline"
                >
                  {T("sejourPaymentInfoLink", "Voir les informations CMI / Visa / MasterCard / 3DS")}
                </a>
              </div>
              </div>
            </section>
            )}

            {/* ===== Navigation wizard ===== */}
            <div className="flex items-center justify-between gap-4">
              {formStep > 0 ? (
                <button
                  type="button"
                  onClick={formBack}
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-black uppercase tracking-widest text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm transition-all"
                >
                  ← {T("wizBack", "Précédent")}
                </button>
              ) : (
                <span />
              )}

              {formStep < lastFormStep ? (
                <button
                  type="button"
                  onClick={formNext}
                  className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-black uppercase tracking-widest text-sm shadow-lg hover:from-orange-600 hover:to-red-700 transition-all"
                >
                  {T("wizNext", "Suivant")} →
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || !acceptCgv}
                  className={`inline-flex items-center gap-3 px-10 py-4 rounded-xl font-black uppercase tracking-widest text-sm shadow-lg transition-all ${loading || !acceptCgv
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
                    }`}
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                      {T("sejourSending", "Envoi en cours...")}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {T("sejourSubmitBtn", "Soumettre ma demande")}
                    </>
                  )}
                </button>
              )}
            </div>

            {formStep === lastFormStep && !acceptCgv && (
              <p className="text-xs text-gray-500 mt-3 text-center">
                {T("sejourMustAccept", "Pour envoyer la demande, vous devez d’abord accepter les conditions (CMI/CGV).")}
              </p>
            )}

            <p className="text-xs text-gray-500 mt-3 max-w-2xl mx-auto text-center">
              {T(
                "sejourSubmitHint",
                "Après soumission, un agent traitera votre demande, sélectionnera l’offre la plus adaptée, fixera le tarif final et vous enverra un email avec un lien de paiement sécurisé."
              )}
            </p>
          </motion.form>
        )}
      </div>
    </div>
  );
};

export default SejourPage;
